import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { err, ok, type Result } from '@shared/result';
import { asId, type PersonId } from '@shared/brand';
import type {
  IPersonalRecordRepository,
  RepositoryError,
  SavePersonalRecordInput,
} from '@application/ports';
import type { PersonalRecord } from '@domain/entities/PersonalRecord';
import { isBloodType, isEducationLevel, isMaritalStatus } from '@domain/entities/PersonalRecord';
import { COLLECTIONS, firebase } from '../firebase/client';
import { translateCallableError, translateFirestoreError } from './support';

/**
 * El expediente personal, contra Firestore.
 *
 * **Se lee directo y se escribe por callable**, igual que el salario: la lectura
 * la gobiernan las reglas —`ver_expediente` dentro del área—, y la escritura pasa
 * por el servidor porque hay que revalidar el permiso y dejar rastro. Un
 * expediente que cambia sin autor no se puede auditar, y éste guarda datos que
 * alguien podría querer negar haber tocado.
 *
 * **Un documento ausente no es un error.** 326 de 337 contestaron el formulario:
 * quien no lo hizo simplemente no tiene expediente todavía, y devolver `null` es
 * decir eso. Un `not-found` obligaría a cada llamador a distinguir el hueco
 * normal del fallo real.
 */
export class FirestorePersonalRecordRepository implements IPersonalRecordRepository {
  async getFor(personId: PersonId): Promise<Result<PersonalRecord | null, RepositoryError>> {
    try {
      const { db } = firebase();
      const snapshot = await getDoc(doc(db, COLLECTIONS.personalRecords, personId));
      if (!snapshot.exists()) return ok(null);
      return ok(decodePersonalRecord(snapshot.id, snapshot.data()));
    } catch (cause) {
      return err(translateFirestoreError(cause, `${COLLECTIONS.personalRecords}/${personId}`));
    }
  }

  async save(input: SavePersonalRecordInput): Promise<Result<void, RepositoryError>> {
    try {
      const { functions } = firebase();
      const invoke = httpsCallable<SavePersonalRecordInput, void>(functions, 'savePersonalRecord');
      await invoke(input);
      return ok(undefined);
    } catch (cause) {
      return err(translateCallableError(cause, 'savePersonalRecord'));
    }
  }
}

/**
 * Todo campo desconocido cae a `null` en vez de propagarse.
 *
 * El expediente llega de un formulario y de una carga masiva, así que un
 * `maritalStatus` que diga «Soltero(a)» en vez de `soltero` es una posibilidad
 * real. Descartar el valor y enseñar «Sin registrar» es preferible a pintar en la
 * ficha una cadena que nadie eligió.
 */
const decodePersonalRecord = (id: string, data: Record<string, unknown>): PersonalRecord => {
  const texto = (value: unknown): string => (typeof value === 'string' ? value : '');
  const fecha = (value: unknown): Date | null => {
    if (value !== null && typeof value === 'object' && 'toDate' in value) {
      return (value as { toDate: () => Date }).toDate();
    }
    return null;
  };

  return {
    personId: asId<'PersonId'>(id),
    maritalStatus: isMaritalStatus(data['maritalStatus']) ? data['maritalStatus'] : null,
    hasInfonavitCredit:
      typeof data['hasInfonavitCredit'] === 'boolean' ? data['hasInfonavitCredit'] : null,
    educationLevel: isEducationLevel(data['educationLevel']) ? data['educationLevel'] : null,
    bloodType: isBloodType(data['bloodType']) ? data['bloodType'] : null,
    children: typeof data['children'] === 'number' ? data['children'] : null,
    allergies: texto(data['allergies']),
    personalEmail: texto(data['personalEmail']),
    personalPhone: texto(data['personalPhone']),
    privacyAcceptedAt: fecha(data['privacyAcceptedAt']),
    updatedAt: fecha(data['updatedAt']) ?? new Date(0),
    updatedByPersonId:
      typeof data['updatedByPersonId'] === 'string'
        ? asId<'PersonId'>(data['updatedByPersonId'])
        : null,
  };
};
